import { Link } from 'react-router-dom';
import { optimizeImage } from '../lib/cms';

export default function Footer() {
  const logoUrl = optimizeImage('https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-bc37f76e4fec45df81ec63fedfb4e897-mv2.png', 400);

  return (
    <footer className="bg-[#070b10] border-t border-slate-800 text-slate-400 font-sans">
      {/* Upper CTA Banner */}
      <div className="border-b border-slate-800/80 bg-gradient-to-r from-[#0b121c] via-[#0d1624] to-[#0b121c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <span className="text-xs font-open-sans text-[#258cfb] uppercase tracking-widest block mb-2 font-semibold">
                Industriel problemløsning &amp; mekatronik
              </span>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                Klar til at optimere dine produktionsmaskiner?
              </h2>
              <p className="mt-3 text-slate-300 text-base leading-relaxed">
                Uanset om du søger en freelance konstruktør, opbygning af nye maskiner, tavlebygning eller automatisering af manuelle processer, står JARCon klar.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto shrink-0">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#0076cf] hover:bg-[#0088ec] text-white font-bold text-sm tracking-wide transition-all shadow-lg shadow-[#0076cf]/30 active:scale-[0.98]"
              >
                <span>Kontakt mig i dag</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <a
                href="tel:+4522884536"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-slate-900/90 hover:bg-slate-800 text-slate-200 border border-slate-700/80 hover:border-[#0076cf]/50 font-semibold text-sm transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#258cfb] text-xs" aria-hidden="true" />
                <span className="whitespace-nowrap">+45 22 88 45 36</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 4-Column Footer Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Column 1: Brand Logo, Availability, Primary CTA Button, Operating Hours, Email, Social */}
          <div className="space-y-5">
            <Link to="/" className="inline-block focus:outline-none focus:ring-2 focus:ring-[#0076cf] rounded">
              <img
                src={logoUrl}
                alt="JARCon ApS"
                className="h-12 w-auto object-contain filter brightness-110"
              />
            </Link>

            {/* Availability Indicator */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-xs font-medium text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <span>Klar til nye opgaver &amp; projekter</span>
            </div>

            {/* Highly Contrasting Primary CTA Button with Icon */}
            <div>
              <Link
                to="/kontakt"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-5 py-3 rounded-lg bg-[#0076cf] hover:bg-[#0088ec] text-white font-bold text-sm tracking-wide transition-all shadow-md shadow-[#0076cf]/30 active:scale-[0.98]"
              >
                <span>Få en teknisk vurdering</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

            {/* Operating Hours (utilizing accent color for the times) */}
            <div className="pt-2 border-t border-slate-800/80 space-y-1.5 text-xs text-slate-300">
              <span className="text-slate-400 font-bold uppercase tracking-wider text-[11px] block">
                Åbningstider
              </span>
              <div className="flex justify-between items-center max-w-[240px]">
                <span className="text-slate-400">Man – Tors:</span>
                <span className="text-[#258cfb] font-semibold">07:00 – 16:00</span>
              </div>
              <div className="flex justify-between items-center max-w-[240px]">
                <span className="text-slate-400">Fredag:</span>
                <span className="text-[#258cfb] font-semibold">07:00 – 14:00</span>
              </div>
              <div className="flex justify-between items-center max-w-[240px]">
                <span className="text-slate-400">Weekend:</span>
                <span className="text-slate-500">Efter aftale</span>
              </div>
            </div>

            {/* Email Address with Icon */}
            <div className="pt-1">
              <a
                href="mailto:jens@jarcon.dk"
                className="inline-flex items-center gap-2.5 text-sm text-slate-200 hover:text-[#258cfb] transition-colors whitespace-nowrap"
              >
                <span className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-[#258cfb] shrink-0">
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                </span>
                <span className="whitespace-nowrap font-medium">jens@jarcon.dk</span>
              </a>
            </div>

            {/* Row of Social Media / Direct Link Icons */}
            <div className="flex items-center gap-2.5 pt-1">
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 rounded-lg bg-slate-900/90 border border-slate-800 flex items-center justify-center text-slate-300 hover:text-white hover:border-[#0076cf] hover:bg-[#0076cf]/20 transition-all text-sm"
              >
                <i className="fa-brands fa-linkedin-in" aria-hidden="true" />
              </a>
              <a
                href="tel:+4522884536"
                aria-label="Telefon"
                className="w-9 h-9 rounded-lg bg-slate-900/90 border border-slate-800 flex items-center justify-center text-slate-300 hover:text-white hover:border-[#0076cf] hover:bg-[#0076cf]/20 transition-all text-sm"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </a>
              <a
                href="mailto:jens@jarcon.dk"
                aria-label="Email"
                className="w-9 h-9 rounded-lg bg-slate-900/90 border border-slate-800 flex items-center justify-center text-slate-300 hover:text-white hover:border-[#0076cf] hover:bg-[#0076cf]/20 transition-all text-sm"
              >
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Column 2: Location Addresses & Registration Number */}
          <div className="space-y-4">
            <h3 className="text-white font-display text-base font-bold tracking-tight pb-1 border-b border-slate-800 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#0076cf] rounded-full"></span>
              Virksomhed &amp; Lokationer
            </h3>

            <div className="space-y-3.5 text-sm">
              <div>
                <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-1">
                  Adresse
                </h4>
                <p className="text-slate-300">Toftehøj 21</p>
                <p className="text-slate-400 text-xs">6470 Sydals, Danmark</p>
              </div>

              <div className="pt-2 border-t border-slate-800/60">
                <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-1">
                  Produktionsadresse
                </h4>
                <p className="text-slate-300">Huholt 1A</p>
                <p className="text-slate-400 text-xs">6400 Sønderborg, Danmark</p>
              </div>

              <div className="pt-2 border-t border-slate-800/60">
                <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-1">
                  Registrering
                </h4>
                <p className="text-slate-300 flex items-center gap-2">
                  <span className="text-slate-400 text-xs">CVR:</span>
                  <span className="font-medium text-white">39150255</span>
                </p>
              </div>

              <div className="pt-2 border-t border-slate-800/60">
                <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-1">
                  Direkte kontakt
                </h4>
                <div className="text-slate-300 text-xs space-y-1">
                  <span className="block">
                    Telefon:{' '}
                    <a href="tel:+4522884536" className="text-white font-medium hover:text-[#258cfb] transition-colors whitespace-nowrap">
                      +45 22 88 45 36
                    </a>
                  </span>
                  <span className="block">
                    Email:{' '}
                    <a href="mailto:jens@jarcon.dk" className="text-white font-medium hover:text-[#258cfb] transition-colors whitespace-nowrap">
                      jens@jarcon.dk
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Column 3: Konsulentydelser Links List */}
          <div className="space-y-4">
            <h3 className="text-white font-display text-base font-bold tracking-tight pb-1 border-b border-slate-800 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#0076cf] rounded-full"></span>
              Konsulentydelser
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/konsulentydelser" className="hover:text-[#258cfb] transition-colors">
                  Konsulentydelser oversigt
                </Link>
              </li>
              <li>
                <Link to="/ydelser/mekanisk-konstruktion" className="hover:text-[#258cfb] transition-colors">
                  Mekanisk konstruktion
                </Link>
              </li>
              <li>
                <Link to="/ydelser/elkonstruktion" className="hover:text-[#258cfb] transition-colors">
                  Elkonstruktion &amp; styretavler
                </Link>
              </li>
              <li>
                <Link to="/ydelser/cad-fem" className="hover:text-[#258cfb] transition-colors">
                  CAD design og FEM (Inventor, NX, Creo)
                </Link>
              </li>
              <li>
                <Link to="/ydelser/montage-indkoring" className="hover:text-[#258cfb] transition-colors">
                  Montage og indkøring
                </Link>
              </li>
              <li>
                <Link to="/ydelser/dokumentation-ce" className="hover:text-[#258cfb] transition-colors">
                  Dokumentation &amp; CE mærkning
                </Link>
              </li>
            </ul>

            <div className="mt-4 pt-3 border-t border-slate-800">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                Tekniske CAD systemer
              </span>
              <p className="text-xs text-slate-400">
                Autodesk Inventor • Siemens NX • PTC Creo • PC Schematik
              </p>
            </div>
          </div>

          {/* Column 4: Automation & Sider Links List */}
          <div className="space-y-4">
            <h3 className="text-white font-display text-base font-bold tracking-tight pb-1 border-b border-slate-800 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#0076cf] rounded-full"></span>
              Automation &amp; Sider
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/automation" className="hover:text-[#258cfb] transition-colors">
                  Automation &amp; robotter
                </Link>
              </li>
              <li>
                <Link to="/automation/automatiske-maskiner" className="hover:text-[#258cfb] transition-colors">
                  Automatiske maskiner
                </Link>
              </li>
              <li>
                <Link to="/komplette-losninger" className="hover:text-[#258cfb] transition-colors">
                  Komplette løsninger
                </Link>
              </li>
              <li>
                <Link to="/produktionsudstyr" className="hover:text-[#258cfb] transition-colors">
                  Produktionsudstyr
                </Link>
              </li>
              <li>
                <Link to="/vaerktoj" className="hover:text-[#258cfb] transition-colors">
                  Værktøj &amp; fiksturer
                </Link>
              </li>
              <li>
                <Link to="/projekter" className="hover:text-[#258cfb] transition-colors">
                  Projekter &amp; referencer
                </Link>
              </li>
              <li>
                <Link to="/om-mig" className="hover:text-[#258cfb] transition-colors">
                  Om JARCon (Jens Christensen)
                </Link>
              </li>
              <li>
                <Link to="/cykelservice" className="hover:text-[#258cfb] transition-colors">
                  Cykelservice
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-[#258cfb] transition-colors">
                  Kontakt JARCon ApS
                </Link>
              </li>
              <li className="pt-2 mt-2 border-t border-slate-800/80">
                <a
                  href="https://d6e713fb-6411-4075-ab6a-5936b7a50d5a.sites.auxodev.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors"
                  title="JARC Audio - Hifi & lyd (privat projekt)"
                >
                  <i className="fa-solid fa-volume-high text-cyan-500 text-xs" aria-hidden="true" />
                  <span>JARC Audio (Hifi projekt)</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[9px] text-slate-500" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom copyright line - NO cookies or privacy policy per rules */}
        <div className="mt-12 pt-8 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 font-open-sans gap-4">
          <p>© {new Date().getFullYear()} JARCon ApS • CVR: 39150255 • Alle rettigheder forbeholdes.</p>
          <div className="flex items-center gap-4">
            <span className="text-slate-400">Toftehøj 21, 6470 Sydals &amp; Huholt 1A, 6400 Sønderborg</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
